import {
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import {
  Button,
  Card,
  ConfigProvider,
  Dropdown,
  Input,
  InputRef,
  Tooltip,
  Typography,
} from 'antd'
import {
  copyTextToClipboard,
  DEFAULT_EXPLORATION_NAME,
  DrilldownDataContext,
  useDrilldownPanelData,
} from 'features/drilldown'
import { ExplorerDrawerConfigPanel } from 'features/drilldown/components/config/ExplorerDrawerConfigPanel'
import { DRILLDOWN_PANEL_CONFIGS } from 'features/drilldown/components/panelConfigs/PanelConfigs'
import { useDrilldownPanelCreation } from 'features/drilldown/data/DrilldownPanelCreationContext'
import { DrilldownPanelType, GenericPanelData } from 'features/drilldown/types'
import { useGeckoNotifications } from 'features/notifications'
import { CopyPlus, Link, Maximize2, Pin } from 'lucide-react'
import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import { FullscreenModal } from './FullscreenModal'
import { PanelContextProvider } from './PanelContext'

const { Text } = Typography

const DEFAULT_CARD_PADDING = 24
const DEFAULT_ICON_COLOR = CantileverTheme.colors.slate['400']

export type DrilldownPanelProps = {
  panelType: DrilldownPanelType
  onClose?: () => void
  icon?: ReactNode
  titleTooltip?: ReactNode
  configPanel?: ReactNode
  panelPadding?: number
  panelId?: string
}

const CARD_STYLE: React.CSSProperties = {
  width: '100%',
}

interface DrilldownPanelTitleProps {
  icon?: ReactNode
  titleTooltip?: ReactNode
}

const HeaderContainer = styled.div`
  justify-content: space-between;
  display: flex;
  flex-direction: row;
  padding-right: 0.1rem;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DrilldownPanelTitle: FC<DrilldownPanelTitleProps> = ({
  icon,
  titleTooltip,
}) => {
  const { isPresentationModeEnabled } = useContext(DrilldownDataContext)
  const { panelData, setPanelData } = useDrilldownPanelData<GenericPanelData>()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const inputFieldRef = useRef<InputRef>(null)

  const { title } = panelData

  const setTitle = useCallback(
    (newTitle: string) => {
      setPanelData((prev: GenericPanelData) => ({ ...prev, title: newTitle }))
    },
    [setPanelData]
  )

  useEffect(() => {
    if (isEditing) {
      inputFieldRef.current?.focus()
    } else {
      inputFieldRef.current?.blur()
    }
  }, [isEditing])

  return (
    <HeaderContainer>
      <TitleContainer>
        {!!icon && <span style={{ color: DEFAULT_ICON_COLOR }}>{icon}</span>}
        {!isEditing && <Text>{title}</Text>}
        <Input
          style={{ display: isEditing ? 'inline' : 'none' }}
          ref={inputFieldRef}
          value={title}
          onBlur={() => setIsEditing(false)}
          onChange={(e) => setTitle(e.target.value)}
          onPressEnter={() => setIsEditing(false)}
        />
        {!!titleTooltip && (
          <Tooltip title={titleTooltip}>
            <InfoCircleOutlined style={{ color: DEFAULT_ICON_COLOR }} />
          </Tooltip>
        )}
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorText: CantileverTheme.colors.grayscale[400],
              },
            },
          }}>
          {!isPresentationModeEnabled && (
            <Button
              shape='circle'
              size='small'
              type='text'
              onClick={() => setIsEditing(true)}>
              <EditOutlined style={{ opacity: 0.4 }} />
            </Button>
          )}
        </ConfigProvider>
      </TitleContainer>
    </HeaderContainer>
  )
}

export const AbstractDrilldownPanel: FC<
  DrilldownPanelProps &
    DrilldownPanelTitleProps & {
      configPanel?: ReactNode
      children: ReactNode
    }
> = ({
  children,
  onClose,
  icon,
  titleTooltip,
  configPanel,
  panelPadding,
  panelId,
  panelType,
}) => {
  const { isPresentationModeEnabled, explorationTitle } =
    useContext(DrilldownDataContext)
  const { panelData, setPanelData, focusPanelId } =
    useDrilldownPanelData<GenericPanelData>()
  const notificationContext = useGeckoNotifications()
  const panelCreationContext = useDrilldownPanelCreation()

  const cardRef = useRef<HTMLDivElement>(null)
  const [isFullscreenModalOpen, setIsFullscreenModalOpen] = useState(false)
  const [shouldRenderFullscreenContent, setShouldRenderFullscreenContent] =
    useState(false)
  const hasBeenManuallyClosed = useRef(false)

  const duplicatePanel = useCallback(() => {
    panelCreationContext.createPanels([
      {
        panelType: panelType,
        initialDataOverride: { ...panelData },
      },
    ])
  }, [panelCreationContext, panelData, panelType])

  useEffect(() => {
    setTimeout(
      () => cardRef.current?.scrollIntoView({ behavior: 'smooth' }),
      50
    )
  }, [])

  // Auto-opens full screen modal (if the panel is mentioned in the URL)
  useEffect(() => {
    if (
      focusPanelId &&
      focusPanelId === panelId &&
      !isFullscreenModalOpen &&
      !hasBeenManuallyClosed.current
    ) {
      setIsFullscreenModalOpen(true)
    }
  }, [focusPanelId, panelId, isFullscreenModalOpen])

  useEffect(() => {
    hasBeenManuallyClosed.current = false
  }, [focusPanelId])

  // Rerender the panel content after the full screen modal animation.
  // This is to handle an edge case where the site view render is smaller than the modal,
  // seemingly because it tries to render the panel content before the modal is fully open.
  useEffect(() => {
    if (isFullscreenModalOpen) {
      const timer = setTimeout(() => {
        setShouldRenderFullscreenContent(true)
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [isFullscreenModalOpen])

  const handleOpenInFullscreen = useCallback(() => {
    setIsFullscreenModalOpen(true)
  }, [])

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreenModalOpen(false)
    setShouldRenderFullscreenContent(false)
    hasBeenManuallyClosed.current = true
  }, [setIsFullscreenModalOpen])

  const handleConfigToggle = useCallback(() => {
    setPanelData((prev) => ({
      ...prev,
      isConfigOpen: !prev.isConfigOpen,
    }))
  }, [setPanelData])

  const handleConfigClose = useCallback(() => {
    setPanelData((prev) => ({
      ...prev,
      isConfigOpen: false,
    }))
  }, [setPanelData])

  const toggleIsPinned = useCallback(() => {
    setPanelData((prev: GenericPanelData) => ({
      ...prev,
      isPinned: !prev.isPinned,
    }))
  }, [setPanelData])

  const handleCopyFullscreenLink = useCallback(async () => {
    if (!panelId) return

    try {
      // Includes exploration id and any other params
      const currentUrl = new URL(window.location.href)

      currentUrl.searchParams.set('focus', panelId)

      await copyTextToClipboard(currentUrl.toString(), () => {
        notificationContext.successNotification(
          'Fullscreen link copied to clipboard'
        )
      })
    } catch (error) {
      console.error('Failed to copy link:', error)
      notificationContext.errorNotification('Failed to copy link to clipboard')
    }
  }, [panelId, notificationContext])

  const canCopyFullscreenLink = Boolean(
    panelId && explorationTitle && explorationTitle !== DEFAULT_EXPLORATION_NAME
  )

  const panelActionMenuItems = useMemo<NonNullable<MenuProps['items']>>(() => {
    const items: NonNullable<MenuProps['items']> = []

    if (!isPresentationModeEnabled) {
      items.push({
        key: 'pin',
        icon: panelData.isPinned ? (
          <Pin fill={CantileverTheme.colors.highVis} size={16} />
        ) : (
          <Pin size={16} />
        ),
        label: panelData.isPinned ? 'Unpin panel' : 'Pin panel',
        onClick: toggleIsPinned,
      })

      items.push({
        key: 'duplicate',
        icon: <CopyPlus size={16} />,
        label: 'Duplicate panel',
        onClick: duplicatePanel,
      })
    }

    if (canCopyFullscreenLink) {
      items.push({
        key: 'copy-link',
        icon: <Link size={16} />,
        label: 'Copy fullscreen link',
        onClick: handleCopyFullscreenLink,
      })
    }

    items.push({
      key: 'fullscreen',
      icon: <Maximize2 size={16} />,
      label: 'Open in fullscreen',
      onClick: handleOpenInFullscreen,
    })

    return items
  }, [
    isPresentationModeEnabled,
    canCopyFullscreenLink,
    handleOpenInFullscreen,
    panelData.isPinned,
    toggleIsPinned,
    duplicatePanel,
    handleCopyFullscreenLink,
  ])

  return (
    <PanelContextProvider isFullscreen={isFullscreenModalOpen}>
      <ConfigProvider
        theme={{
          token: {
            lineWidth: 0.5,
          },
          components: {
            Card: {
              bodyPadding: panelPadding ?? DEFAULT_CARD_PADDING,
            },
          },
        }}>
        <Card
          style={CARD_STYLE}
          variant='borderless'
          ref={cardRef}
          title={
            <DrilldownPanelTitle icon={icon} titleTooltip={titleTooltip} />
          }
          extra={
            <>
              {!!configPanel && !isPresentationModeEnabled && (
                <Button
                  type={panelData.isConfigOpen ? 'primary' : 'text'}
                  icon={<SettingOutlined />}
                  onClick={handleConfigToggle}
                  title='Panel configuration'
                />
              )}
              <Dropdown
                menu={{ items: panelActionMenuItems }}
                trigger={['hover']}>
                <Button
                  size='small'
                  type='text'
                  icon={<MoreOutlined />}
                  title='Panel actions'
                />
              </Dropdown>
              {!!onClose && !isPresentationModeEnabled && (
                <Button
                  type='text'
                  icon={<CloseOutlined />}
                  onClick={onClose}
                />
              )}
            </>
          }>
          {children}
        </Card>
      </ConfigProvider>

      {!!configPanel && (
        <ExplorerDrawerConfigPanel
          open={!!panelData.isConfigOpen}
          onClose={handleConfigClose}
          title='Panel Configuration'>
          {configPanel}
        </ExplorerDrawerConfigPanel>
      )}

      <FullscreenModal
        open={isFullscreenModalOpen}
        onClose={handleCloseFullscreen}
        title={panelData.title}
        icon={icon}>
        {shouldRenderFullscreenContent ? children : null}
      </FullscreenModal>
    </PanelContextProvider>
  )
}

const RawDrilldownPanel: FC<
  DrilldownPanelProps & { panelType: DrilldownPanelType }
> = ({ panelType, ...props }) => {
  const panelSpec = DRILLDOWN_PANEL_CONFIGS[panelType]

  if (!panelSpec || !panelSpec.render) {
    throw new Error(`Unknown panel type: ${panelType}`)
  }

  const childProps = useMemo(
    () => ({ ...props, icon: panelSpec.icon, panelType }),
    [panelSpec.icon, props, panelType]
  )
  return panelSpec.render(childProps)
}

export const DrilldownPanel = React.memo(RawDrilldownPanel)
